<template>
  <div class="t-app__container">
    <div class="h-section h-section--no-margin-bottom">
      <button type="button" class="a-btn-primary-outline" @click="goToDashboard">{{ $t('edit-businee-go-back-btn') }}</button>
    </div>
    <heading :title="name" />
    <div class="h-section h-section--large h-section--no-margin-top">
      <div class="h-grid h-grid--justify-content-center">
        <div class="h-grid__col h-grid__col--sm-10 h-grid__col--md-8">
          <s-form @submit="saveBusiness">
            <fieldset class="m-form-fieldset">
              <legend class="a-title-regular a-title-regular--800">{{ $t('edit-business-general-info-title') }}</legend>
              <p class="m-form-group">
                <label class="a-form-label" for="name">{{ $t('label-business-name') }}*</label>
                <input class="a-form-control" id="name" v-model.trim="$v.name.$model" autocomplete="edit-business business-name" />
                <span v-if="$v.name.$dirty">
                  <span class="m-form-group__error" v-if="!$v.name.required">{{ $t('error-field-required') }}</span>
                </span>
              </p>
              <p class="m-form-group">
                <label class="a-form-label" for="image-picker">
                  {{ $t('edit-business-image') }}
                  <span class="m-form-group__note m-form-group__note--small">{{ $t('edit-business-image-description') }}</span>
                </label>
                <image-picker id="image-picker" uniqueKey="businessHeaderImagePicker" v-model="imageFile" v-bind:imageOriginalUrl="imageOriginalUrl" @removeFile="removeImage" />
              </p>
              <div class="m-form-group">
                <entrepreneurs-checkbox v-if="initialBusinessValue.Id" v-model.trim="$v.entrepreneurIds.$model" :businessId="initialBusinessValue.Id">
                  <template>
                    <span v-if="$v.entrepreneurIds.$dirty">
                      <span class="m-form-group__error" v-if="!$v.entrepreneurIds.required">{{ $t('error-field-required') }}</span>
                    </span>
                  </template>
                </entrepreneurs-checkbox>
              </div>
              <p class="m-form-group">
                <label class="a-form-label" for="shortDescription">{{ $t('label-edit-business-shortDescription') }}*</label>
                <input class="a-form-control" id="shortDescription" v-model.trim="$v.shortDescription.$model" autocomplete="edit-business short-description" />
                <span v-if="$v.shortDescription.$dirty">
                  <span class="m-form-group__error" v-if="!$v.shortDescription.required">{{ $t('error-field-required') }}</span>
                </span>
              </p>
              <p class="m-form-group">
                <business-category-select v-model.trim="$v.category.$model" />
                <span v-if="$v.category.$dirty">
                  <span class="m-form-group__error" v-if="!$v.category.minValue">{{ $t('error-field-required') }}</span>
                </span>
              </p>
              <p class="m-form-group">
                <span class="a-form-label">{{ $t('label-edit-business-location') }}*</span>
                <span class="a-form-radio a-form-radio--block">
                  <input type="radio" name="locationType" id="rally" value="gathering" v-model="locationType" autocomplete="edit-business location-type-gathering" />
                  <label for="rally">{{ $t('label-location-type-gathering') }}</label>
                </span>
                <span class="a-form-radio a-form-radio--block">
                  <input type="radio" id="address" name="locationType" value="address" v-model="locationType" autocomplete="edit-business location-type-address" />
                  <label for="address">{{ $t('label-location-type-personal') }}</label>
                </span>
              </p>
              <p class="warning" v-show="!isRegistered && !mustRegisterToPublish && isLocationTypeGathering">{{ $t('Business.PaymentWarning') }}</p>
              <p class="m-form-group__note" v-html="$t('gathering-map-link')" v-show="isLocationTypeGathering"></p>
              <gathering-autocomplete v-model.trim="$v.gatheringId.$model" v-show="isLocationTypeGathering" @quitGathering="displayQuitGatheringPopup">
                <template>
                  <span v-if="$v.gatheringId.$dirty">
                    <span class="m-form-group__error" v-if="!$v.gatheringId.gatheringValidator">{{ $t('error-field-required') }}</span>
                  </span>
                </template>
              </gathering-autocomplete>
              <p class="m-form-group" v-show="showPrivatePasswordField">
                <label class="a-form-label" for="privatePassword"> {{ $t('label-edit-gathering-private-password') }}<required-form-field /></label>
                <input class="a-form-control" id="privatePassword" v-model.trim="privatePassword" />
                <span v-if="$v.privatePassword.$dirty">
                  <span class="m-form-group__error" v-if="!$v.privatePassword.privatePasswordIsRequired">{{ $t('error-field-required') }}</span>
                </span>
              </p>
              <div>
                <div class="o-panel" v-bind:class="{ 'o-panel--open': isQuitGatheringPanelOpen }">
                  <div class="o-panel__head">
                    <h2 class="o-panel__title">{{ $t('quit-current-gathering-btn') }}</h2>
                    <button type="button" class="o-panel__close" @click="hideQuitGatheringPopup">{{ $t('panel-close') }}</button>
                  </div>
                  <div class="o-panel__content">
                    <confirmation-popup :text="$t('quit-gathering-confirmation-text')" :actions="quitGatheringActions" />
                  </div>
                </div>
                <div class="o-panel__overlay" @click="hideQuitGatheringPopup"></div>
              </div>
              <div>
                <div class="o-panel" v-bind:class="{ 'o-panel--open': isWrongPrivatePasswordErrorPopupOpen }">
                  <div class="o-panel__head">
                    <h2 class="o-panel__title">{{ $t('gathering-private-password-error') }}</h2>
                    <button type="button" class="o-panel__close" @click="hideWrongPrivatePasswordErrorPopup">{{ $t('panel-close') }}</button>
                  </div>
                  <div class="o-panel__content">
                    <confirmation-popup :text="$t('gathering-wrong-private-password-error-text')" :actions="gatheringPrivatePasswordErrorActions" />
                  </div>
                </div>
                <div class="o-panel__overlay" @click="hideWrongPrivatePasswordErrorPopup"></div>
              </div>
              <div>
                <div class="o-panel" v-bind:class="{ 'o-panel--open': isGatheringRegistrationClosedErrorPopupOpen }">
                  <div class="o-panel__head">
                    <h2 class="o-panel__title">{{ $t('gathering-registration-closed-error') }}</h2>
                    <button type="button" class="o-panel__close" @click="hideRegistrationClosedErrorPopup">{{ $t('panel-close') }}</button>
                  </div>
                  <div class="o-panel__content">
                    <confirmation-popup :text="$t('gathering-registration-closed-error-text')" :actions="gatheringPrivatePasswordErrorActions" />
                  </div>
                </div>
                <div class="o-panel__overlay" @click="hideRegistrationClosedErrorPopup"></div>
              </div>
              <div v-show="!isLocationTypeGathering">
                <p v-if="gatheringId !== -1">
                  <span class="m-form-group__note" v-html="$t('quit-gathering-text')"></span>
                  <a href="javascript:void();" class="edit-business__remove-link" @click="displayQuitGatheringPopup">
                    <span class="edit-business__remove-icon"></span>{{ $t('quit-gathering-byname-btn', { gatheringName }) }}</a
                  >
                </p>
                <googlemap-autocomplete @place="setPlace" v-model.trim="$v.address.$model" :placeholder="$t('placeholder-business-address')">
                  <template>
                    <span v-if="$v.address.$dirty || $v.latitude.dirty">
                      <span class="m-form-group__error" v-if="!$v.address.requiredIf">{{ $t('error-field-required') }}</span>
                      <span class="m-form-group__error" v-if="!$v.latitude.requiredIf">{{ $t('error-lat-long-required') }}</span>
                    </span>
                  </template>
                </googlemap-autocomplete>
                <span class="a-form-checkbox a-form-checkbox--block">
                  <input type="checkbox" id="hideAddress" v-model="hideAddress" autocomplete="edit-business hide-address" />
                  <label for="hideAddress">{{ $t('business-hide-address-label') }}</label>
                </span>
                <administrative-region-select v-model.trim="$v.region.$model">
                  <span v-if="$v.region.$dirty">
                    <span class="m-form-group__error" v-if="!$v.region.regionIsValid">{{ $t('error-field-required') }}</span>
                  </span>
                </administrative-region-select>
              </div>
            </fieldset>

            <fieldset class="m-form-fieldset">
              <legend class="a-title-regular a-title-regular--800">{{ $t('edit-business-detail-info-title') }}</legend>
              <p class="m-form-group">
                <label class="a-form-label" for="description">
                  {{ $t('label-edit-business-description') }}
                  <span class="m-form-group__note m-form-group__note--small">{{ $t('detail-edit-business-description') }}</span>
                </label>
                <rich-text-editor v-if="description !== null" v-model="description" />
              </p>

              <div class="m-form-group">
                <label class="a-form-label" for="photoGallery">
                  {{ $t('label-edit-business-photoGallery') }}
                  <span class="m-form-group__note m-form-group__note--small">{{ $t('label-edit-business-photoGallery-description') }}</span>
                </label>
                <span v-for="photo in photoGalleryFiles" :key="photo.Id" class="edit-business__image">
                  <img :src="photo.PictureUri" width="100%" />
                  <button type="button" @click="() => removePictureFromGallery(photo.Id)" class="edit-business__image-delete">X</button>
                </span>
                <image-picker v-if="showAddPhotoGallery" uniqueKey="businessGalleryImagePicker" id="photoGallery" display-save v-model="photoGalleryFile" @saveFile="savePhotoGalleryFile" />
                <p>
                  <a href="javascript:void();" v-if="!showAddPhotoGallery" @click="openAddPhotoGallery">{{ $t('add-photo') }}</a>
                </p>
              </div>

              <span class="a-form-label">{{ $t('edit-business-open-hours-title') }}</span>
              <div v-if="gatheringId === '' || gatheringId === -1" class="m-form-group">
                <p>
                  <label class="a-form-label" for="openingTime">
                    <span class="m-form-group__note m-form-group__note--small">{{ $t('label-edit-business-openingTime') }}*</span>
                  </label>
                  <vue-timepicker
                    class="a-form-control"
                    id="openingTime"
                    hide-disabled-items
                    :hour-range="[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18]"
                    :minute-interval="15"
                    v-model.trim="$v.openingTime.$model"
                  ></vue-timepicker>
                  <span v-if="$v.openingTime.$dirty">
                    <span class="m-form-group__error" v-if="!$v.openingTime.requiredIf || !$v.openingTime.timeValidator">{{ $t('error-field-required') }}</span>
                  </span>
                </p>
                <p>
                  <label class="a-form-label" for="closingTime">
                    <span class="m-form-group__note m-form-group__note--small">{{ $t('label-edit-business-closingTime') }}*</span>
                  </label>
                  <vue-timepicker
                    class="a-form-control"
                    id="closingTime"
                    hide-disabled-items
                    :hour-range="[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18]"
                    :minute-interval="15"
                    v-model="$v.closingTime.$model"
                  ></vue-timepicker>
                  <span v-if="$v.closingTime.$dirty">
                    <span class="m-form-group__error" v-if="!$v.closingTime.requiredIf || !$v.closingTime.timeValidator">{{ $t('error-field-required') }}</span>
                  </span>
                </p>
              </div>
              <div v-else-if="gathering !== null && gatheringId !== -1 && gatheringName !== null" class="m-form-group__note">
                <p>{{ $t('text-gathering-openingClosingTime') }}</p>
                <p>
                  <strong>{{ $t('edit-gathering-overtitle') }} {{ gatheringName }}</strong
                  ><br />
                  {{ gatheringOpeningTime }}{{ $t('text-gathering-time') }}{{ gatheringClosingTime }}
                </p>
              </div>

              <p class="m-form-group">
                <label class="a-form-label" for="facebookUrl">{{ $t('label-edit-business-facebookUrl') }}</label>
                <input class="a-form-control" id="facebookUrl" v-model="$v.facebookUrl.$model" autocomplete="edit-business facebook-url" />
                <span v-if="$v.facebookUrl.$dirty">
                  <span class="m-form-group__error" v-if="!$v.facebookUrl.mustBeFacebookUrl">{{ $t('error-field-required') }}</span>
                </span>
              </p>

              <p class="m-form-group">
                <label class="a-form-label" for="instagramUrl">{{ $t('label-edit-business-instagramUrl') }}</label>
                <input class="a-form-control" id="instagramUrl" v-model.trim="$v.instagramUrl.$model" autocomplete="edit-business instagram-url" />
                <span v-if="$v.instagramUrl.$dirty">
                  <span class="m-form-group__error" v-if="!$v.instagramUrl.mustBeInstagramUrl">{{ $t('error-field-required') }}</span>
                </span>
              </p>

              <fieldset class="m-form-fieldset">
                <legend class="a-title-regular a-title-regular--800">{{ $t('edit-business-products-title') }}</legend>
                <div class="m-form-group">
                  <div v-for="(product, index) in products" :key="index">
                    <p>
                      <span>{{ product.Name }}</span
                      ><br />
                      <span>{{ product.Description }}</span
                      ><br />
                      <span>{{ product.Price }}$</span>
                    </p>
                    <p>
                      <a href="javascript:void();" @click="() => editProduct(index)">
                        {{ $t('edit-business-product-edit') }}
                      </a>
                      <a href="javascript:void();" class="edit-business__remove-link" @click="() => deleteProduct(index)">
                        <span class="edit-business__remove-icon"></span>{{ $t('edit-business-product-delete') }}
                      </a>
                    </p>
                  </div>
                  <div v-if="isProductPanelOpen" class="o-panel" v-bind:class="{ 'o-panel--open': isProductPanelOpen }">
                    <div class="o-panel__head">
                      <h2 class="o-panel__title">{{ selectedProduct ? $t('title-edit-product') : $t('title-add-product') }}</h2>
                      <button type="button" class="o-panel__close" @click="hideAddProductPanel">{{ $t('panel-close') }}</button>
                    </div>
                    <div class="o-panel__content">
                      <product v-if="isProductPanelOpen" @saveProduct="saveProduct" :productEditing="selectedProduct" />
                    </div>
                  </div>
                  <div class="o-panel__overlay" @click="hideAddProductPanel"></div>
                  <p>
                    <a href="javascript:void();" @click="showAddProductPanel">{{ $t('edit-business-product-add') }}</a>
                  </p>
                </div>
              </fieldset>

              <hr />

              <h2 class="a-form-subtitle">{{ $t('form-new-feature-title') }}</h2>

              <p class="m-form-group">
                <label class="a-form-label" for="onlineStoreUrl">{{ $t('label-edit-business-onlineStoreUrl') }}</label>
                <span class="m-form-group__note m-form-group__note--small" v-html="$t('label-edit-business-onlineStoreUrl-note')" />
                <input
                  class="a-form-control"
                  id="onlineStoreUrl"
                  v-model.trim="$v.onlineStoreUrl.$model"
                  autocomplete="edit-business online-store-url"
                  :placeholder="$t('label-edit-business-onlineStoreUrl-placeholder')"
                />
                <span v-if="$v.onlineStoreUrl.$dirty">
                  <span class="m-form-group__error" v-if="!$v.onlineStoreUrl.url">{{ $t('invalid-url') }}</span>
                </span>
              </p>
            </fieldset>

            <fieldset v-if="badges.length > 0" class="m-form-fieldset">
              <legend class="a-title-regular a-title-regular--800">{{ $t('edit-business-badges-title') }}</legend>
              <span class="m-form-group__note m-form-group__note--small" v-html="$t('edit-business-badges-description')" />
              <div class="m-form-group">
                <span v-for="badge in badges" :key="badge.Name" class="a-form-checkbox a-form-checkbox--block">
                  <input type="checkbox" :id="badge.Name" v-model="badge.Checked" />
                  <label :for="badge.Name">{{ badge.Name }}</label>
                </span>
              </div>
            </fieldset>

            <hr />

            <div
              class="m-form-group--error"
              v-if="
                !$v.name.required ||
                  !$v.entrepreneurIds.required ||
                  !$v.shortDescription.required ||
                  !$v.category.minValue ||
                  !$v.address.requiredIf ||
                  !$v.latitude.requiredIf ||
                  !$v.region.regionIsValid ||
                  !$v.openingTime.requiredIf ||
                  !$v.openingTime.timeValidator ||
                  !$v.closingTime.requiredIf ||
                  !$v.closingTime.timeValidator ||
                  !$v.facebookUrl.mustBeFacebookUrl ||
                  !$v.gatheringId.gatheringValidator ||
                  !$v.instagramUrl.mustBeInstagramUrl ||
                  !$v.onlineStoreUrl.url ||
                  !$v.privatePassword.privatePasswordIsRequired
              "
            >
              <strong>{{ $t('business-errors-message') }}</strong>
              <ul>
                <li v-if="!$v.name.required">{{ $t('business-name-error-text') }}</li>
                <li v-if="!$v.entrepreneurIds.required">{{ $t('business-entrepreneurs-error-text') }}</li>
                <li v-if="!$v.shortDescription.required">{{ $t('business-shortDescription-error-text') }}</li>
                <li v-if="!$v.category.minValue">{{ $t('business-category-error-text') }}</li>
                <li v-if="!$v.address.requiredIf">{{ $t('business-adress-error-text') }}</li>
                <li v-if="!$v.latitude.requiredIf">{{ $t('business-adress-lat-long-error-text') }}</li>
                <li v-if="!$v.region.regionIsValid">{{ $t('business-region-error-text') }}</li>
                <li v-if="!$v.openingTime.requiredIf || !$v.openingTime.timeValidator || !$v.closingTime.requiredIf || !$v.closingTime.timeValidator">{{ $t('business-openHours-error-text') }}</li>
                <li v-if="!$v.facebookUrl.mustBeFacebookUrl">{{ $t('business-facebook-error-text') }}</li>
                <li v-if="!$v.instagramUrl.mustBeInstagramUrl">{{ $t('business-instagram-error-text') }}</li>
                <li v-if="!$v.onlineStoreUrl.url">{{ $t('business-onlinse-store-invalid-url-error-text') }}</li>
                <li v-if="!$v.gatheringId.gatheringValidator">{{ $t('gathering-error-text') }}</li>
                <li v-if="!$v.privatePassword.privatePasswordIsRequired">{{ $t('gathering-private-password-is-required-error-text') }}</li>
              </ul>
            </div>

            <span class="m-form-group--error" v-if="!isRegistered && mustRegisterToPublish">{{ $t('Business.MustRegisterToPublish') }}</span>

            <p class="m-form-group m-form-group--center edit-business__actions">
              <button v-if="!isPublish" class="a-btn-primary-outline" type="submit">
                {{ $t('button-save-business-as-draft') }}
              </button>
              <button v-else class="a-btn-primary" type="submit">
                {{ $t('button-save-business') }}
              </button>
              <button v-if="!isPublish" :disabled="!isRegistered && mustRegisterToPublish" class="a-btn-primary" style="margin-left: 20px;" type="button" @click="publishBusiness">
                {{ !isRegistered && !mustRegisterToPublish ? $t('button-pay-and-publish-business') : $t('button-publish-business') }}
              </button>
              <button v-else class="a-btn-primary-outline" style="margin-left: 20px;" type="button" @click="unpublishBusiness">
                {{ $t('button-unpublish-business') }}
              </button>
              <button v-if="!isRegistered && (mustRegisterToPublish || isPublish)" class="a-btn-primary" @click="registerBusiness" style="margin-left: 20px;" type="button">
                {{ $t('business-register-btn') }}
              </button>
            </p>

            <p class="m-form-group m-form-group--center edit-business__actions">
              <a href="javascript:void();" @click="previewBusiness">{{ $t('button-preview-business') }}</a>
            </p>

            <div class="o-panel" v-bind:class="{ 'o-panel--open': isImpossibleDraftSaveGatheringPanelOpen }">
              <div class="o-panel__head">
                <h2 class="o-panel__title">{{ $t('impossible-draft-save-gathering-title') }}</h2>
                <button type="button" class="o-panel__close" @click="hideImpossibleDraftSaveGatheringPanel">{{ $t('panel-close') }}</button>
              </div>
              <div class="o-panel__content">
                <confirmation-popup :text="$t('impossible-draft-save-gathering-text')" :actions="impossibleDraftSaveGatheringActions" />
              </div>
            </div>
            <div class="o-panel__overlay" @click="hideImpossibleDraftSaveGatheringPanel"></div>

            <div class="o-panel" v-bind:class="{ 'o-panel--open': isImpossibleUnpublishGatheringPanelOpen }">
              <div class="o-panel__head">
                <h2 class="o-panel__title">{{ $t('impossible-unpublish-gathering-title') }}</h2>
                <button type="button" class="o-panel__close" @click="hideImpossibleUnpublishGatheringPanel">{{ $t('panel-close') }}</button>
              </div>
              <div class="o-panel__content">
                <confirmation-popup :text="$t('impossible-unpublish-gathering-text')" :actions="impossibleUnpublishGatheringActions" />
              </div>
            </div>
            <div class="o-panel__overlay" @click="hideImpossibleUnpublishGatheringPanel"></div>
          </s-form>
        </div>
      </div>
    </div>
    <div class="t-app__confetti t-app__confetti--01"></div>
    <div class="t-app__dot t-app__dot--large"></div>
    <div class="t-app__confetti t-app__confetti--02"></div>
    <div class="t-app__confetti t-app__confetti--03"></div>
    <div class="t-app__confetti t-app__confetti--04"></div>
    <div class="t-app__dot t-app__dot--medium"></div>
    <div class="t-app__confetti t-app__confetti--05"></div>
  </div>
</template>

<script>
import { required, minValue, requiredIf, url } from 'vuelidate/lib/validators'
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue'

import Heading from '@/components/heading'
import SForm from '@/components/form/form'
import BusinessCategorySelect from '@/components/form/business-category-select'
import EntrepreneursCheckbox from '@/components/form/entrepreneurs-checkbox'
import GooglemapAutocomplete from '@/components/form/googlemap-autocomplete'
import Product from '@/components/form/product'
import ImagePicker from '@/components/form/image-picker'
import AdministrativeRegionSelect from '@/components/form/administrative-region-select'
import GatheringAutocomplete from '@/components/form/gathering-autocomplete'
import ConfirmationPopup from '@/components/popup/confirmation-popup'
import RichTextEditor from '@/components/form/rich-text-editor'
import RequiredFormField from '@/components/ui/required-form-field'

import router from '@/router'
import { URL_USER_PARTICIPATE_DASHBOARD, URL_ADMIN_BUSINESS_DASHBOARD } from '@/consts/urls'
import { USER_TYPE_ADMIN } from '@/consts/enums'
import Store from '@/stores/store'

import BusinessService from '@/services/business'
import LoaderService from '@/services/loader'
import GatheringService from '@/services/gathering'
import ImageUpload from '@/services/image-upload'
import AuthentificationService from '@/services/authentification'
import SnipcartService from '@/services/snipcart'

const LOCATION_TYPE_GATHERING = 'gathering'
const LOCATION_TYPE_PERSONAL = 'address'

const mustBeFacebookUrl = value =>
  value === '' || value === null || value.indexOf('https://facebook.com/') >= 0 || value.indexOf('https://www.facebook.com/') >= 0 || value.indexOf('https://m.facebook.com/') >= 0
const mustBeInstagramUrl = value => value === '' || value === null || value.indexOf('https://www.instagram.com/') >= 0
const gatheringValidator = function(value) {
  if (this.isLocationTypeGathering) {
    return value != -1
  }
  return true
}
const timeValidator = function(value) {
  if (value.indexOf(':mm') !== -1 || value.indexOf('HH:') !== -1) return false
  return true
}
const regionIsValid = function(value) {
  if (!this.isLocationTypeGathering) {
    return value > 0
  }
  return true
}

const privatePasswordIsRequired = function(value) {
  if (this.showPrivatePasswordField && this.isLocationTypeGathering && value === '') return false
  return true
}

export default {
  components: {
    Heading,
    SForm,
    BusinessCategorySelect,
    EntrepreneursCheckbox,
    GooglemapAutocomplete,
    VueTimepicker,
    Product,
    ImagePicker,
    AdministrativeRegionSelect,
    GatheringAutocomplete,
    ConfirmationPopup,
    RichTextEditor,
    RequiredFormField
  },
  data() {
    return {
      imageFile: '',
      imageOriginalUrl: '',
      initialBusinessValue: {},
      initialGatheringValue: {},
      name: '',
      shortDescription: '',
      description: null,
      entrepreneurIds: [],
      category: 0,
      gatheringId: -1,
      address: '',
      hideAddress: false,
      latitude: '',
      longitude: '',
      postalcode: '',
      city: '',
      region: 0,
      locationType: '',
      openingTime: '',
      closingTime: '',
      facebookUrl: '',
      instagramUrl: '',
      onlineStoreUrl: '',
      products: [],
      isProductPanelOpen: false,
      selectedProductIndex: null,
      photoGalleryFiles: [],
      photoGalleryFile: '',
      showAddPhotoGallery: false,
      isQuitGatheringPanelOpen: false,
      quitGatheringActions: [
        { key: 'quitGathering_yes', label: this.$t('popup_confirm_label'), click: this.quitGathering },
        { key: 'quitGathering_no', label: this.$t('popup_dismiss_label'), click: this.hideQuitGatheringPopup }
      ],
      isImpossibleDraftSaveGatheringPanelOpen: false,
      impossibleDraftSaveGatheringActions: [{ key: 'impossible_save_draft_gathering_ok', label: this.$t('popup_understood_label'), click: this.hideImpossibleDraftSaveGatheringPanel }],
      isImpossibleUnpublishGatheringPanelOpen: false,
      impossibleUnpublishGatheringActions: [{ key: 'impossible_unpublish_gathering_ok', label: this.$t('popup_understood_label'), click: this.hideImpossibleUnpublishGatheringPanel }],
      badgeIds: new Set(),
      showPrivatePasswordField: false,
      privatePassword: '',
      isWrongPrivatePasswordErrorPopupOpen: false,
      isGatheringRegistrationClosedErrorPopupOpen: false,
      gatheringPrivatePasswordErrorActions: [{ key: 'closeGatheringPrivatePasswordError_ok', label: this.$t('popup_okay_label'), click: this.hideWrongPrivatePasswordErrorPopup }],
      gatheringRegistrationClosedErrorActions: [{ key: 'closeGatheringRegistrationClosedError_ok', label: this.$t('popup_okay_label'), click: this.hideRegistrationClosedErrorPopup }]
    }
  },
  validations: {
    name: {
      required
    },
    entrepreneurIds: {
      required
    },
    shortDescription: {
      required
    },
    category: {
      required,
      minValue: minValue(1)
    },
    gatheringId: {
      gatheringValidator
    },
    address: {
      requiredIf: requiredIf(function() {
        return !this.isLocationTypeGathering || this.gatheringId == -1
      })
    },
    latitude: {
      requiredIf: requiredIf(function() {
        return !this.isLocationTypeGathering || this.gatheringId == -1
      })
    },
    region: {
      regionIsValid
    },
    openingTime: {
      requiredIf: requiredIf(function() {
        return !this.isLocationTypeGathering
      }),
      timeValidator
    },
    closingTime: {
      requiredIf: requiredIf(function() {
        return !this.isLocationTypeGathering
      }),
      timeValidator
    },
    facebookUrl: {
      mustBeFacebookUrl
    },
    instagramUrl: {
      mustBeInstagramUrl
    },
    onlineStoreUrl: {
      url
    },
    privatePassword: {
      privatePasswordIsRequired
    }
  },
  async beforeRouteEnter(to, from, next) {
    LoaderService.showLoader()
    const business = await BusinessService.getBusinessById(to.params.id)

    let gathering = {}
    if (business.GatheringId !== -1 && business.GatheringId !== '') {
      gathering = await GatheringService.getGatheringById(business.GatheringId)
    }
    LoaderService.hideLoader()

    next(vm => {
      vm.name = business.Name
      vm.shortDescription = business.ShortDescription
      vm.description = business.Description === null ? '' : business.Description
      vm.category = business.Category
      vm.entrepreneurIds = business.EntrepreneurIds.slice()
      vm.gatheringId = business.GatheringId === '' ? -1 : business.GatheringId
      vm.address = business.Address
      vm.latitude = business.Latitude
      vm.longitude = business.Longitude
      vm.hideAddress = business.HideAddress
      vm.openingTime = business.OpeningTime === null ? '' : business.OpeningTime
      vm.closingTime = business.ClosingTime === null ? '' : business.ClosingTime
      vm.facebookUrl = business.FacebookUrl
      vm.instagramUrl = business.InstagramUrl
      vm.onlineStoreUrl = business.OnlineStoreUrl
      vm.products = business.Products
      vm.imageOriginalUrl = business.ImageUri
      vm.photoGalleryFiles = business.PhotoGalleryFile
      vm.showAddPhotoGallery = business.PhotoGalleryFile.length === 0
      vm.region = business.Region
      vm.badgeIds = new Set(business.BadgeIds)

      vm.initialBusinessValue = business
      vm.initialGatheringValue = gathering

      vm.locationType =
        (business.GatheringId !== -1 && business.GatheringId !== null && business.GatheringId !== '') || business.Address === '' || business.Address === null
          ? LOCATION_TYPE_GATHERING
          : LOCATION_TYPE_PERSONAL
    })
  },
  computed: {
    badges: function() {
      const badges = Store.getters.badges
      const self = this
      badges.forEach(badge => {
        badge.Checked = self.badgeIds.has(badge.UmbracoId)
      })
      return badges
    },
    isLocationTypeGathering: function() {
      return this.locationType === LOCATION_TYPE_GATHERING
    },
    gatheringName: function() {
      return this.initialGatheringValue.Name
    },
    gatheringOpeningTime: function() {
      return this.initialGatheringValue.OpeningTime
    },
    gatheringClosingTime: function() {
      return this.initialGatheringValue.ClosingTime
    },
    isPublish: function() {
      return this.initialBusinessValue.IsPublish
    },
    isRegistered: function() {
      return this.initialBusinessValue.IsRegistered
    },
    mustRegisterToPublish: function() {
      return this.initialBusinessValue.MustRegisterToPublish
    },
    selectedProduct: function() {
      return this.products[this.selectedProductIndex]
    }
  },
  methods: {
    registerBusiness: function() {
      SnipcartService.addBusinessToCart(this.initialBusinessValue.Id, this.initialBusinessValue.Name, false)
      this.goToDashboard()
    },
    gatheringChanged: async function() {
      this.privatePassword = ''

      if (this.gatheringId !== -1 && this.gatheringId !== this.initialBusinessValue.GatheringId) {
        const isPublic = await GatheringService.getIsPublicByGatheringId(this.gatheringId)
        this.showPrivatePasswordField = !isPublic
        return
      }

      this.showPrivatePasswordField = false
    },
    hideWrongPrivatePasswordErrorPopup: function() {
      this.isWrongPrivatePasswordErrorPopupOpen = false
    },
    hideRegistrationClosedErrorPopup: function() {
      this.isGatheringRegistrationClosedErrorPopupOpen = false
    },
    goToDashboard: function() {
      if (AuthentificationService.getUserType() === USER_TYPE_ADMIN) {
        router.push({ name: URL_ADMIN_BUSINESS_DASHBOARD })
      } else {
        router.push({ name: URL_USER_PARTICIPATE_DASHBOARD })
      }
    },
    displayQuitGatheringPopup: function() {
      this.isQuitGatheringPanelOpen = true
    },
    quitGathering: function() {
      this.gatheringId = -1
      this.isQuitGatheringPanelOpen = false
    },
    hideQuitGatheringPopup: function() {
      this.isQuitGatheringPanelOpen = false
    },
    showAddProductPanel: function() {
      this.isProductPanelOpen = true
    },
    hideAddProductPanel: function() {
      this.selectedProductIndex = null
      this.isProductPanelOpen = false
    },
    hideImpossibleDraftSaveGatheringPanel: function() {
      this.isImpossibleDraftSaveGatheringPanelOpen = false
    },
    hideImpossibleUnpublishGatheringPanel: function() {
      this.isImpossibleUnpublishGatheringPanelOpen = false
    },
    saveProduct: function(product) {
      this.isProductPanelOpen = false

      if (this.selectedProduct) {
        product.Id = this.selectedProduct.Id
        this.products[this.selectedProductIndex] = product
      } else {
        this.products.push(product)
      }

      this.selectedProductIndex = null
    },
    editProduct: function(index) {
      this.selectedProductIndex = index
      this.isProductPanelOpen = true
    },
    deleteProduct: function(index) {
      this.products.splice(index, 1)
    },
    refreshSelectedGathering: async function() {
      if (this.gatheringId === -1) {
        this.initialGatheringValue = {}
      } else {
        this.initialGatheringValue = await GatheringService.getGatheringById(this.gatheringId)
      }
    },
    previewBusiness: async function() {
      await this.saveBusiness()
      window.location.href = this.initialBusinessValue.Url + '?isPreview=true'
    },
    updateImage: function(imageFile) {
      this.imageFile = imageFile
    },
    removeImage: function() {
      this.imageOriginalUrl = null
      this.imageFile = ''
    },
    openAddPhotoGallery: function() {
      this.showAddPhotoGallery = true
    },
    removePictureFromGallery: function(photoId) {
      this.photoGalleryFiles = this.photoGalleryFiles.filter(x => x.Id !== photoId)
    },
    savePhotoGalleryFile: async function() {
      LoaderService.showLoader()

      let request = {
        businessId: this.initialBusinessValue.Id
      }

      let result = null
      if (this.photoGalleryFile !== null && this.photoGalleryFile !== '') {
        result = await ImageUpload.sendImage(this.photoGalleryFile)
        let value = this.photoGalleryFiles.map(x => x.PictureFileId)
        value.push(result.fileId)
        request.photoGalleryFileIds = { value }
      }

      let response = await BusinessService.updateBusiness(request)
      this.initialBusinessValue = response
      this.photoGalleryFiles = response.PhotoGalleryFile
      this.photoGalleryFile = ''
      this.showAddPhotoGallery = false

      LoaderService.hideLoader()
    },
    saveBusiness: async function(publish = false) {
      if (this.isPublish) {
        this.$v.$touch()
        if (this.$v.$invalid) {
          return
        }
      } else if (this.gatheringId !== -1 && !publish) {
        this.isImpossibleDraftSaveGatheringPanelOpen = true
        return
      }

      LoaderService.showLoader()
      let request = {
        businessId: this.initialBusinessValue.Id
      }

      let result = null
      if (this.imageFile !== null && this.imageFile !== '') {
        result = await ImageUpload.sendImage(this.imageFile)
        request.imageFileId = { value: result.fileId }
      } else if (this.imageFile === '' && this.imageOriginalUrl === null) {
        request.imageFileId = { value: null }
      }

      if (this.name !== this.initialBusinessValue.Name) {
        request.name = { value: this.name }
      }

      if (this.shortDescription !== this.initialBusinessValue.ShortDescription) {
        request.shortDescription = { value: this.shortDescription }
      }

      if (this.category !== this.initialBusinessValue.Category) {
        request.category = { value: this.category }
      }

      if (this.description !== this.initialBusinessValue.Description) {
        request.description = { value: this.description }
      }

      if (this.entrepreneurIds !== this.initialBusinessValue.EntrepreneurIds) {
        request.entrepreneurIds = { value: this.entrepreneurIds }
      }

      if (this.gatheringId !== this.initialBusinessValue.GatheringId) {
        request.gatheringId = { value: this.gatheringId !== -1 ? this.gatheringId : '' }
      }

      if (this.isLocationTypeGathering && this.privatePassword !== '') {
        request.privatePassword = { value: this.privatePassword }
      }

      if (this.address !== this.initialBusinessValue.Address) {
        request.address = { value: this.address }
        request.latitude = { value: this.latitude }
        request.longitude = { value: this.longitude }
        request.postalcode = { value: this.postalcode }
        request.city = { value: this.city }
      }

      if (this.hideAddress !== this.initialBusinessValue.HideAddress) {
        request.hideAddress = { value: this.hideAddress }
      }

      if (this.region !== this.initialBusinessValue.Region) {
        request.region = { value: this.region }
      }

      if (this.openingTime !== this.initialBusinessValue.OpeningTime) {
        request.openingTime = { value: this.openingTime }
      }

      if (this.closingTime !== this.initialBusinessValue.ClosingTime) {
        request.closingTime = { value: this.closingTime }
      }

      if (this.facebookUrl !== this.initialBusinessValue.FacebookUrl) {
        request.facebookUrl = { value: this.facebookUrl }
      }

      if (this.instagramUrl !== this.initialBusinessValue.InstagramUrl) {
        request.instagramUrl = { value: this.instagramUrl }
      }

      if (this.onlineStoreUrl !== this.initialBusinessValue.OnlineStoreUrl) {
        request.onlineStoreUrl = { value: this.onlineStoreUrl }
      }

      const badges = new Set()
      this.badges.forEach(badge => {
        if (badge.Checked) {
          badges.add(badge.UmbracoId)
        }
      })

      const initialBadges = new Set(this.initialBusinessValue.BadgeIds)

      if (!this.compareSets(badges, initialBadges)) {
        request.badgeIds = { value: Array.from(badges) }
      }

      request.products = {
        value: this.products.map(x => {
          let originalId = x.Id
          let product = { name: x.Name, description: x.Description, price: x.Price }
          if (originalId !== undefined && originalId !== null) {
            product.originalId = originalId
          } else {
            product.originalId = ''
          }

          return product
        })
      }

      if (this.photoGalleryFiles.length !== this.initialBusinessValue.PhotoGalleryFile.length) {
        request.photoGalleryFileIds = { value: this.photoGalleryFiles.map(x => x.PictureFileId) }
      }

      let response = publish ? await BusinessService.publishBusiness(request, this.isRegistered) : await BusinessService.updateBusiness(request)

      this.isWrongPrivatePasswordErrorPopupOpen = response.ShowInvalidPasswordError
      this.isGatheringRegistrationClosedErrorPopupOpen = response.ShowGatheringRegistrationClosedError

      this.initialBusinessValue = response
      this.products = response.Products
      LoaderService.hideLoader()
      if (this.products.length > 0) {
        this.isProductPanelOpen = false
      }

      if (publish && !this.isRegistered && !response.ShowInvalidPasswordError && !response.ShowGatheringRegistrationClosedError) {
        this.registerBusiness()
      }
    },
    compareSets: function(setA, setB) {
      if (setA.size !== setB.size) {
        return false
      }

      let same = true
      setA.forEach(a => {
        if (!setB.has(a)) {
          same = false
          return
        }
      })

      return same
    },
    publishBusiness: async function() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        await this.saveBusiness(true)
      }
    },
    unpublishBusiness: async function() {
      if (this.gatheringId !== -1) {
        this.isImpossibleUnpublishGatheringPanelOpen = true
        return
      }
      LoaderService.showLoader()
      let request = {
        businessId: this.initialBusinessValue.Id
      }
      let response = await BusinessService.unpublishBusiness(request)
      this.initialBusinessValue = response
      LoaderService.hideLoader()
    },
    setPlace(place) {
      this.longitude = place.geometry.location.lng()
      this.latitude = place.geometry.location.lat()
      let vm = this

      place.address_components.forEach(function(c) {
        switch (c.types[0]) {
          case 'locality':
            vm.city = c.long_name
            break
          case 'postal_code':
            vm.postalcode = c.long_name
            break
        }
      })
    }
  },
  watch: {
    gatheringId: function(val) {
      this.refreshSelectedGathering()
      this.gatheringChanged()
    }
  }
}
</script>
<style lang="scss">
.edit-business {
  &__image {
    display: inline-block;
    margin: 0 10px 10px 0;
    position: relative;
    width: 100px;

    &-delete {
      appearance: none;
      background: #e00c05 url(~@/assets/delete.svg) no-repeat center center;
      background-size: 15px 15px;
      border: 0;
      border-radius: 50%;
      color: transparent;
      cursor: pointer;
      font: 0/0 a;
      height: 26px;
      padding: 0;
      position: absolute;
      right: 10px;
      text-shadow: none;
      top: 10px;
      white-space: nowrap;
      width: 26px;
    }
  }

  &__remove {
    &-link {
      color: #e00c05;

      &:hover,
      &:focus,
      &:active {
        color: darken(#e00c05, 15%);
      }
    }

    &-icon {
      appearance: none;
      background: #e00c05 url(~@/assets/remove.svg) no-repeat center center;
      background-size: 10px 10px;
      border: 0;
      border-radius: 50%;
      cursor: pointer;
      display: inline-block;
      height: 18px;
      margin: -8px 5px 0 15px;
      vertical-align: middle;
      width: 18px;
    }
  }

  &__actions {
    * {
      margin-bottom: 12px;
    }
  }
}

.display-time {
  border: 0 !important;
  color: inherit;
  font-family: 'Proxima', sans-serif;
  height: auto !important;
  outline: none;
  padding: 0 !important;
  width: 100% !important;
}

.vue__time-picker .dropdown ul li:not([disabled]).active,
.vue__time-picker .dropdown ul li:not([disabled]).active:hover,
.vue__time-picker .dropdown ul li:not([disabled]).active:focus {
  background: #7667a7;
}

.vs__dropdown-toggle {
  border: 0;
  border-radius: none;
  padding: 0;
}

.warning {
  border: 2px solid #edb539;
  margin: 20px 0;
  padding: 10px;
  text-align: center;
  width: 100%;
}
</style>
